import React, { useState } from "react";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { Pagination } from "@sscs/types";
import {
  Button,
  Card,
  DataTable,
  hasActiveFilters,
  useFilterSortPagination,
} from "@sscs/ui";
import { AddUsageLimitIcon as AddUsageIcon } from "@sscs/ui/icons";
import { getClassName, getQueryStatus } from "@sscs/utils";
import { AllowedTo } from "Components";
import { PERMISSION } from "Constants";
import {
  GetOrganizationUsageResponse,
  GroupUsage as GroupUsageType,
} from "Types";
import { useGetGroupsUsageQuery } from "../../services/licensing.api";
import { FilterGroups, SortGroups } from "../../types";
import AddLimit from "../AddLimit/AddLimit";
import GroupRow from "../GroupRow/GroupRow";
import GroupsFiltersAndActions from "../GroupsFiltersAndActions/GroupsFiltersAndActions";
import {
  GroupsTableTitle,
  useGroupsColumns,
} from "../GroupsTableHeader/GroupsTableHeader";
import styles from "./groupUsage.module.scss";

const initFilter: FilterGroups = {
  name: "",
  hasLimit: "false",
};

const initSort: SortGroups = {
  property: "used",
  reverse: true,
};

const initPagination: Pagination = {
  currentPage: 1,
  itemsPerPage: 10,
};

type GroupUsageProps = {
  organizationUsage?: GetOrganizationUsageResponse;
};

const GroupUsage = ({
  organizationUsage,
}: GroupUsageProps): React.JSX.Element | null => {
  const [limit, setLimit] = useState<{
    open: boolean;
    groupUsage: GroupUsageType | null;
  }>({ open: false, groupUsage: null });
  const {
    filter,
    sort,
    pagination,
    handleFilterChange,
    handleSortChange,
    handlePaginationChange,
  } = useFilterSortPagination(initFilter, initSort, initPagination);
  const {
    data = { match_count: 0, groups: [] },
    isFetching,
    isLoading,
    isError,
    error,
  } = useGetGroupsUsageQuery(
    organizationUsage
      ? {
          organizationId: organizationUsage.organization_id,
          groupName: filter.name,
          hasLimit: filter.hasLimit,
          sortBy: `${sort.reverse ? "-" : ""}${sort.property}`,
          offset: (pagination.currentPage - 1) * pagination.itemsPerPage,
          limit: pagination.itemsPerPage,
        }
      : skipToken,
  );
  const [focusedRow, setFocusedRow] = useState(0);
  const columns = useGroupsColumns();

  if (!organizationUsage) return null;

  const limitDisabled = organizationUsage.groups_without_limits.length === 0;

  return (
    <>
      <AddLimit
        organizationUsage={organizationUsage}
        groupUsage={limit.groupUsage}
        isOpen={limit.open}
        onClose={() =>
          setLimit({
            open: false,
            groupUsage: null,
          })
        }
      />
      <Card.Card>
        <Card.Title
          as="h2"
          actions={
            <AllowedTo permissions={[PERMISSION.ADD_GROUP_CAPACITY_LIMIT]}>
              <Button
                color="primary"
                className={styles["add-limit"]}
                disabled={limitDisabled}
                onClick={() =>
                  setLimit({
                    open: true,
                    groupUsage: null,
                  })
                }
              >
                <AddUsageIcon
                  className={getClassName(styles.icon, {
                    [styles["icon-disabled"]]: limitDisabled,
                  })}
                />
                Add group limit
              </Button>
            </AllowedTo>
          }
        >
          <GroupsTableTitle />
        </Card.Title>
        <Card.Content flush>
          <GroupsFiltersAndActions
            filter={filter}
            found={{
              id: "group-usage-clear-all-filters",
              count: data.match_count,
              initialFilters: initFilter,
              activeFilters: filter,
              onClear: () => handleFilterChange(initFilter),
            }}
            onFilterChange={handleFilterChange}
          />
          <DataTable
            chrome="plain"
            columns={columns}
            sort={sort}
            onSortChange={handleSortChange}
            empty={{
              subject: "groups",
              filters: {
                active: hasActiveFilters(initFilter, filter),
                onReset: () => handleFilterChange(initFilter),
              },
            }}
            pagination={{
              itemCount: data?.match_count ?? 0,
              pagination,
              onChange: handlePaginationChange,
            }}
            status={getQueryStatus({
              isLoading,
              isFetching,
              isError,
              error,
              data,
            })}
          >
            {data.groups.map((group, index) => (
              <GroupRow
                key={group.group_id}
                group={group}
                focused={focusedRow === index}
                filterGroupName={filter.name}
                onFocusChange={() => setFocusedRow(index)}
                onAddLimit={() =>
                  setLimit({
                    open: true,
                    groupUsage: group,
                  })
                }
              />
            ))}
          </DataTable>
        </Card.Content>
      </Card.Card>
    </>
  );
};

export default GroupUsage;
